import usePrior from '@/hooks/use-prior.hook';
import useThread from '@/hooks/use-thread.hook';
import { Box, Divider, Stack, TextField, Theme } from '@mui/material';
import {
  useEffect,
  useState,
  useRef,
  KeyboardEvent,
  useCallback,
  ChangeEvent,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { createUseStyles } from 'react-jss';
import UploadTray, { UploadTrayInstance } from './upload-tray';
import FlatButton from '@/components/flat-button';
import ThreadToastTray from './thread-toast-tray';
import { makeDeckFileMessage } from '@/lib/services/thread.service';
import { ApiFileResponse } from '@/lib/services/index';
import { scrollToCursor } from '@/lib/helpers';
import ThreadSettingsTray from './thread-settings-tray';
import useMessageTray from '@/hooks/use-message-tray.hook';
import { addAction } from '@/lib/datadog';
import useEmitter from '@/hooks/use-emitter.hook';
import { EmitterEventData } from '@/contexts/emitter/helpers';
import ThreadToolsTray from './thread-tools-tray';
import ToolChip from './tool-chip';
import useUserSettings from '@/hooks/use-user-settings.hook';
import Icon from '@/components/icon';
import { getBreakpoint } from '@/theme';

const useStyles = createUseStyles<string>((theme: Theme) => ({
  messageControl: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    backgroundColor:
      theme.palette.mode === 'dark' ? theme.palette.background.linkbar : theme.palette.common.white,
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
  },
  messageInput: {
    '& .MuiInputBase-root': {
      padding: 0,
      '& textarea': {
        padding: 8,
      },
      '& fieldset': {
        border: 'none !important',
      },
    },
  },
  toolbar: {
    padding: '4px 8px 8px 8px',
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
  },
  toolbarTools: {
    flex: '1 1 0%',
    minWidth: 0,
    overflowX: 'auto',
  },
  toolbarActions: {
    flexShrink: 0,
  },
  sendButton: {
    '& svg': {
      color: theme.palette.mode === 'dark' ? '#232323fa' : '#fffffffa',
    },
    borderRadius: theme.shape.borderRadius,
    marginLeft: 16,
  },
  toolChips: {
    lineHeight: '100%',
  },
  [getBreakpoint('md')]: {
    sendButton: {
      marginLeft: 4,
    },
  },
}));

export interface MessageControlInstance {
  loadContent: (content: string) => void;
  loadFiles: (files: FileList) => void;
  focus: () => void;
}

const MessageControl = forwardRef<MessageControlInstance>((_props, ref) => {
  const [message, setMessage] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);
  const uploadRef = useRef<UploadTrayInstance>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { closeAll } = useMessageTray();
  const { subscribe, unsubscribe } = useEmitter();
  const { publicSearchAvailable } = useUserSettings();

  const {
    thread,
    sendMessage,
    status: threadStatus,
    addMessage,
    closeStream,
    settings: { publicSearchEnabled },
    togglePublicSearch,
  } = useThread();
  const threadOpen = threadStatus === 'open';
  const styles = useStyles();

  const hasOnDeckShortcut = useMemo(() => {
    return thread.conversation.some(({ onDeck, shortcut }) => onDeck && !!shortcut);
  }, [thread]);

  const canSend = useMemo(
    () => !!message && threadOpen && !hasOnDeckShortcut,
    [message, threadOpen, hasOnDeckShortcut]
  );

  useImperativeHandle(ref, () => {
    return {
      focus: () => {
        inputRef.current?.focus();
      },
      loadContent: (content: string) => {
        setMessage(content);
        inputRef.current?.focus();
      },
      loadFiles: (files: FileList) => {
        uploadRef.current?.loadFiles(files);
      },
    };
  }, []);

  // add a newline when pressing enter+shift|ctrl|meta
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' || !inputRef.current) {
      return;
    }

    event.preventDefault();

    if (event.ctrlKey || event.metaKey || event.shiftKey) {
      const textarea = inputRef.current;
      const start = textarea.selectionStart || 0;
      const end = textarea.selectionEnd || 0;

      setMessage((old) => `${old.substring(0, start)}\n${old.substring(end)}`);

      // place the cursor after the newline
      setTimeout(() => {
        textarea.selectionStart = start + 1;
        textarea.selectionEnd = start + 1;
        scrollToCursor(textarea);
      }, 10);
      return;
    }

    handleSendMessage();
  };

  const handleMessageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setMessage(event.target.value);
  };

  const handleSendMessage = useCallback(() => {
    if (!canSend) {
      return;
    }

    closeAll();

    sendMessage(message);

    inputRef.current?.focus();
    inputRef.current?.setSelectionRange(0, 0);
    setMessage('');
  }, [message, sendMessage, canSend, closeAll]);

  const handleUpload = useCallback(
    (file: ApiFileResponse) => {
      const { name, summary } = file;
      if (!name) {
        return;
      }

      addAction('upload_file', { threadId: thread.id });

      const uploadMessage = makeDeckFileMessage(thread.id, name, summary || '');
      addMessage(uploadMessage);
    },
    [addMessage, thread.id]
  );

  const handleCancelMessage = () => {
    closeStream();
  };

  const handleStreamError = useCallback((event: EmitterEventData['stream-error']) => {
    if (!inputRef.current || inputRef.current.value) {
      return;
    }

    setMessage(event?.message?.content || '');
    inputRef.current?.focus();
  }, []);

  const handleDisablePublicSearch = useCallback(() => {
    togglePublicSearch(false);
  }, [togglePublicSearch]);

  const priorThread = usePrior(thread);
  useEffect(() => {
    if (thread.id && priorThread?.id && thread.id !== priorThread.id) {
      inputRef.current?.focus();
    }
  }, [thread.id, priorThread?.id]);

  useEffect(() => {
    subscribe('stream-error', handleStreamError);

    return () => {
      unsubscribe('stream-error', handleStreamError);
    };
  }, [subscribe, unsubscribe, handleStreamError]);

  const locked = !!thread.locked;

  return (
    <Box className={styles.messageControl} ref={containerRef}>
      <TextField
        id="message"
        name="message"
        className={styles.messageInput}
        variant="outlined"
        onChange={handleMessageChange}
        onKeyDown={handleKeyDown}
        value={message}
        margin="none"
        fullWidth
        type="textarea"
        autoFocus
        multiline
        minRows={2}
        maxRows={8}
        placeholder="Enter your message"
        inputRef={inputRef}
        disabled={locked}
        inputProps={{
          maxLength: 40000,
          tabIndex: 1,
          'data-testid': 'send-message-input',
        }}
      />
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="flex-end"
        gap={1}
        className={styles.toolbar}
      >
        <Stack
          direction="row"
          className={styles.toolbarTools}
          justifyContent="flex-start"
          gap={1}
          alignItems="flex-end"
        >
          {publicSearchAvailable && (
            <ThreadToolsTray containerRef={containerRef} disabled={locked} />
          )}
          <ThreadSettingsTray containerRef={containerRef} disabled={locked} />
          {publicSearchEnabled && (
            <Stack direction="row" justifyContent="flex-start" gap={1} className={styles.toolChips}>
              <Divider orientation="vertical" flexItem />
              <ToolChip label="Search" icon="globe" onClick={handleDisablePublicSearch} />
            </Stack>
          )}
        </Stack>

        <Stack direction="row" className={styles.toolbarActions} gap={1} alignItems="flex-end">
          {!threadOpen && (
            <FlatButton icon="cancel" label="Cancel" onClick={handleCancelMessage} size="small" />
          )}

          <UploadTray
            key={`thread-${thread.id}`}
            threadId={thread.id}
            ref={uploadRef}
            containerRef={containerRef}
            onUpload={handleUpload}
            disabled={locked}
          />

          <Icon
            name="arrow-up"
            size="small"
            onClick={handleSendMessage}
            disabled={!canSend}
            background="first"
            className={styles.sendButton}
            testId="send-message-button"
          />
        </Stack>
      </Stack>
      <ThreadToastTray containerRef={containerRef} />
    </Box>
  );
});

export default MessageControl;
